import React, { FC } from 'react';
import styled from 'styled-components';
import { ProductGroupTile } from '../ProductGroupTile';
import { ProductGroupStatisticsWithActions } from './ProductGroupStatisticsWithActions';
import { ProductOut, UsageStatisticsOut } from '../../../../store/api';
import { useTranslation } from 'react-i18next';
import { ProductGroupSlider } from '../../../../shared/common/ProductGroupSlider/ProductGroupSlider';
import { THEME_PRIMARY, THEME_WARNING, themeColor } from '../../../../styles/basics/colors';
import { SingleAddOptions } from '../../../../../models/ListContentCheckOptions';
import { ProductMetaData } from '../../../../../models/Product';
import { mapLoanFormatToObject } from '../../../../../models/LoanFormat';
import Utils from '../../../../shared/utils';
import { useAuthentication } from '../../../../app-store/authentication/useAuthentication';

const StContainer = styled.div`
    margin-bottom: 20px;
`;

const StTitle = styled.span`
    font-size: 18px;
`;

const StActiveLabel = styled.span<{ active: boolean }>`
    color: ${props => (props.active ? themeColor(THEME_PRIMARY) : themeColor(THEME_WARNING))};
    font-weight: bold;
`;

const StHeaderline = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StHeaderSubline = styled.div`
    line-height: 12px;
    margin-bottom: 5px;
`;

export interface ProductGroupContainerProps {
    usageStatistics?: UsageStatisticsOut;
    title?: string;
    active: boolean;
    expirationInDays?: number;
    libraryManagedSlotNumber: number;
    lastEditedEpochMillis?: number;
    moveDown: (slotId: any) => void;
    moveUp: (slotId: any) => void;
    productGroupId?: number;
    productGroupSliderHeight?: number;
    productGroupSliderHeightHover: number;
    sliderProducts: ProductOut[];
    numberOfProductGroupsInScope: number;
    className?: string;
    onAddPrice: (options: SingleAddOptions) => Promise<void>;
}

export const ProductGroupContainer: FC<ProductGroupContainerProps> = ({
    usageStatistics,
    title,
    active,
    expirationInDays,
    libraryManagedSlotNumber,
    lastEditedEpochMillis,
    moveDown,
    moveUp,
    className,
    productGroupSliderHeight,
    productGroupSliderHeightHover,
    sliderProducts,
    productGroupId,
    numberOfProductGroupsInScope,
    onAddPrice
}) => {
    const { t } = useTranslation();
    const authentication = useAuthentication();

    const activeLabel = (
        <StActiveLabel active={active}>
            {active ? t('marketing.productGroups.active') : t('marketing.productGroups.inactive')}
        </StActiveLabel>
    );

    const sublineInner = active ? (
        expirationInDays ? (
            <span>
                &nbsp;
                {t('marketing.productGroups.expireDays', {
                    expirationInDays: expirationInDays
                })}
            </span>
        ) : (
            <span data-testid="last-edited">
                &nbsp;({t('marketing.productGroups.lastEdited', { val: lastEditedEpochMillis ?? 0 })})
            </span>
        )
    ) : null;

    return (
        <StContainer className={className}>
            <>
                <StHeaderline>
                    <div>
                        <StTitle data-testid="product-group-title">{title}</StTitle>
                    </div>
                    {usageStatistics && productGroupId ? (
                        <ProductGroupStatisticsWithActions
                            usageStatistics={usageStatistics}
                            libraryManagedSlotNumber={libraryManagedSlotNumber}
                            moveDown={moveDown}
                            moveUp={moveUp}
                            productGroupId={productGroupId}
                            numberOfProductGroupsInScope={numberOfProductGroupsInScope}
                        />
                    ) : null}
                </StHeaderline>
                <StHeaderSubline>
                    {activeLabel}
                    {sublineInner}
                </StHeaderSubline>
            </>
            <ProductGroupSlider
                productGroupProducts={sliderProducts}
                shadowOffsetX={0}
                sliderHeight={productGroupSliderHeight}
                sliderHeightHover={productGroupSliderHeightHover}
            >
                {product => (
                    <ProductGroupTile
                        isProductSelectable={false}
                        minimalView={false}
                        productSelection={undefined}
                        onAddPrice={onAddPrice}
                        product={product as ProductMetaData}
                        loanFormat={mapLoanFormatToObject(product.loanFormat).key}
                        isInPartnerContext={Utils.isInPartnerContext(authentication)}
                        onProductImageClick={undefined}
                        onSelectProduct={() => {}}
                        isProductSelected={false}
                    />
                )}
            </ProductGroupSlider>
        </StContainer>
    );
};
